/**
 * ImageEditorModal — canvas-based image editor for posts and Stories.
 *
 * Features: crop (5 aspect ratios), drag/zoom within crop frame,
 * 5 filter presets (applied via ctx.filter on export, not CSS-only),
 * draggable text overlays (baked into the exported canvas),
 * undo stack, reset, cancel (preserves original), save (uploads edited blob).
 *
 * Output: JPEG blob uploaded via /api/upload; caller receives new URL.
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Undo2, RotateCcw, Plus, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Constants ──────────────────────────────────────────────────────────── */

const CROP_RATIOS = [
  { key: 'original', label: 'Original', ratio: null },
  { key: '1:1',      label: 'Square',   ratio: 1 },
  { key: '4:5',      label: 'Portrait', ratio: 4 / 5 },
  { key: '9:16',     label: 'Story',    ratio: 9 / 16 },
  { key: '16:9',     label: 'Wide',     ratio: 16 / 9 },
];

// Filters: applied as ctx.filter on canvas export AND as CSS preview.
// All values intentionally restrained (brightness ±8%, saturation ±25%).
const FILTERS = {
  Natural: { css: 'none',                                                                     label: 'Natural' },
  Ember:   { css: 'brightness(1.04) saturate(1.2) sepia(0.15) contrast(1.04)',               label: 'Ember'   },
  Frost:   { css: 'brightness(1.06) saturate(0.8) hue-rotate(-12deg) contrast(1.06)',        label: 'Frost'   },
  Forest:  { css: 'brightness(0.96) saturate(1.25) hue-rotate(22deg) contrast(1.02)',        label: 'Forest'  },
  Dusk:    { css: 'brightness(0.9) saturate(0.72) contrast(1.1) sepia(0.12)',                label: 'Dusk'    },
};

const FONT_STYLES = [
  { key: 'serif',      label: 'Runic',  css: 'Georgia, serif',              canvas: 'Georgia, serif' },
  { key: 'sans-serif', label: 'Clean',  css: 'Inter, Arial, sans-serif',    canvas: 'Arial, sans-serif' },
  { key: 'script',     label: 'Script', css: '"Palatino Linotype", serif',  canvas: '"Palatino Linotype", Palatino, serif' },
];

const TEXT_COLORS = ['#FFFFFF', '#000000', '#F5C842', '#C084FC', '#86EFAC', '#F87171'];

const MAX_EXPORT_DIM = 1500; // natural pixels — prevents upscaling and caps file size

// Profile images: fixed composition, no filters or text. The banner is 3:1 on
// tablet/desktop; phones show its centre at 5:2 (see ProfilePage cover).
const PROFILE_MODES = {
  avatar: { ratio: 1, outW: 800, title: 'Profile picture', save: 'Save picture' },
  banner: { ratio: 3, outW: 1500, title: 'Profile banner', save: 'Save banner' },
};
const BANNER_PHONE_RATIO = 5 / 2;

/** Source rectangle (natural pixels) visible in the crop frame. */
function sourceRect({ frame, display, scale, offset, natural }) {
  const scaledW = display.w * scale;
  const scaledH = display.h * scale;
  const left = frame.w / 2 + offset.x - scaledW / 2;
  const top = frame.h / 2 + offset.y - scaledH / 2;
  const px = natural.w / scaledW;
  const x0 = Math.max(0, -left * px);
  const y0 = Math.max(0, -top * px);
  const x1 = Math.min(natural.w, -left * px + frame.w * px);
  const y1 = Math.min(natural.h, -top * px + frame.h * px);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** The cropped result drawn into a box, for live previews. */
function CropPreview({ src, rect, natural, width, height, round = false, label }) {
  if (!rect || !rect.w) return null;
  const k = width / rect.w;
  // Phones show the centre of a wider crop: shift so the middle stays centred.
  const extraX = (rect.w * k - width) / 2;
  return (
    <figure className="flex flex-col items-center gap-1.5 min-w-0">
      <div className={`relative overflow-hidden bg-black/40 ${round ? 'rounded-full' : 'rounded-md'} ring-1 ring-white/15`} style={{ width, height }}>
        <img src={src} alt="" draggable={false} className="absolute max-w-none select-none pointer-events-none"
          style={{ width: natural.w * k, height: natural.h * k, left: -rect.x * k - extraX, top: -rect.y * k - ((rect.h * k - height) / 2) }} />
      </div>
      {label && <figcaption className="text-[11px] text-muted-foreground">{label}</figcaption>}
    </figure>
  );
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function clampOffset(ox, oy, scale, dw, dh, fw, fh) {
  const sw = dw * scale;
  const sh = dh * scale;
  const maxX = Math.max(0, (sw - fw) / 2);
  const maxY = Math.max(0, (sh - fh) / 2);
  return {
    x: Math.max(-maxX, Math.min(maxX, ox)),
    y: Math.max(-maxY, Math.min(maxY, oy)),
  };
}

function computeFrameSize(areaW, areaH, ratio) {
  const pad = 16;
  const aw = Math.max(1, areaW - pad);
  const ah = Math.max(1, areaH - pad);
  let fw, fh;
  if (ratio >= 1) {
    fw = Math.min(aw, ah * ratio);
    fh = fw / ratio;
  } else {
    fh = Math.min(ah, aw / ratio);
    fw = fh * ratio;
  }
  return { w: Math.round(fw), h: Math.round(fh) };
}

/* ── TextOverlay ─────────────────────────────────────────────────────────── */

function TextOverlay({ text, frameW, frameH, onMove, onDelete, onBeforeMove }) {
  const dragRef = useRef(null);
  const fontStyle = FONT_STYLES.find(f => f.key === text.fontStyle) || FONT_STYLES[0];

  const onPointerDown = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    onBeforeMove();
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      startNX: text.x, startNY: text.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [text.x, text.y, onBeforeMove]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    e.stopPropagation();
    const nx = Math.max(0.05, Math.min(0.95,
      dragRef.current.startNX + (e.clientX - dragRef.current.startX) / frameW));
    const ny = Math.max(0.05, Math.min(0.95,
      dragRef.current.startNY + (e.clientY - dragRef.current.startY) / frameH));
    onMove(text.id, nx, ny);
  }, [text.id, frameW, frameH, onMove]);

  const onPointerUp = useCallback(() => { dragRef.current = null; }, []);

  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        position: 'absolute',
        left: `${text.x * 100}%`,
        top: `${text.y * 100}%`,
        transform: 'translate(-50%, -50%)',
        cursor: 'move',
        touchAction: 'none',
        userSelect: 'none',
        zIndex: 20,
        color: text.color,
        fontSize: text.size,
        fontFamily: fontStyle.css,
        fontWeight: 'bold',
        textShadow: text.shadow
          ? '0 1px 4px rgba(0,0,0,0.85), 1px 1px 2px rgba(0,0,0,0.6)'
          : 'none',
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
        pointerEvents: 'auto',
      }}
    >
      {text.content}
      {/* Delete handle */}
      <button
        onPointerDown={e => { e.stopPropagation(); onDelete(text.id); }}
        style={{
          position: 'absolute', top: -10, right: -10,
          width: 18, height: 18,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.7)',
          border: '1px solid rgba(255,255,255,0.4)',
          color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', lineHeight: 1,
        }}
        aria-label="Delete text"
      >
        <X style={{ width: 10, height: 10 }} />
      </button>
    </div>
  );
}

/* ── ImageEditorModal ────────────────────────────────────────────────────── */

export function ImageEditorModal({ open, onClose, onSave, file, imageUrl, mode = 'post' }) {
  const profile = PROFILE_MODES[mode] || null;
  // ── Image state ──
  const [localUrl, setLocalUrl] = useState('');
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });

  // ── Editor state ──
  const [cropKey, setCropKey]       = useState('original');
  const [imgScale, setImgScale]     = useState(1);
  const [imgOffset, setImgOffset]   = useState({ x: 0, y: 0 });
  const [activeFilter, setFilter]   = useState('Natural');
  const [texts, setTexts]           = useState([]);
  const [history, setHistory]       = useState([]);

  // ── Text add form ──
  const [addingText, setAddingText]   = useState(false);
  const [newText, setNewText]         = useState('');
  const [textFont, setTextFont]       = useState('sans-serif');
  const [textColor, setTextColor]     = useState('#FFFFFF');
  const [textSize, setTextSize]       = useState(26);
  const [textShadow, setTextShadow]   = useState(true);

  const [saving, setSaving] = useState(false);

  // ── Refs ──
  const cropAreaRef  = useRef(null);
  const imgRef       = useRef(null);
  const dragRef      = useRef(null);
  const pinchRef     = useRef(null);
  const activePointersRef = useRef(new Map()); // for pinch detection
  const imgScaleRef  = useRef(imgScale);
  const imgOffsetRef = useRef(imgOffset);
  const textIdRef    = useRef(0);

  useEffect(() => { imgScaleRef.current = imgScale; }, [imgScale]);
  useEffect(() => { imgOffsetRef.current = imgOffset; }, [imgOffset]);

  // ── Crop frame size (responsive via ResizeObserver) ──
  const [cropFrameSize, setCropFrameSize] = useState({ w: 0, h: 0 });
  // The dialog mounts its content a frame after `open` flips; track the element
  // itself so measuring starts once it actually exists.
  const [cropAreaEl, setCropAreaEl] = useState(null);
  const setCropArea = useCallback((el) => { cropAreaRef.current = el; setCropAreaEl(el); }, []);

  const imgLoaded = imgNatural.w > 0;
  const naturalRatio = imgLoaded ? imgNatural.w / imgNatural.h : 1;

  const activeCropRatio = useMemo(() => {
    if (profile) return profile.ratio;
    const r = CROP_RATIOS.find(c => c.key === cropKey);
    return r?.ratio ?? naturalRatio;
  }, [cropKey, naturalRatio, profile]);

  // Recompute crop frame size whenever container, ratio, or image changes
  useEffect(() => {
    const el = cropAreaEl;
    if (!el) return;
    const compute = () => {
      const { width, height } = el.getBoundingClientRect();
      setCropFrameSize(computeFrameSize(width, height, activeCropRatio));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cropAreaEl, activeCropRatio, imgLoaded]);

  // Base display size of image at imgScale=1 (cover the crop frame)
  const imgDisplaySize = useMemo(() => {
    if (!cropFrameSize.w || !imgNatural.w) return { w: 0, h: 0 };
    const sW = cropFrameSize.w / imgNatural.w;
    const sH = cropFrameSize.h / imgNatural.h;
    const s = Math.max(sW, sH); // cover
    return { w: imgNatural.w * s, h: imgNatural.h * s };
  }, [cropFrameSize, imgNatural]);

  // ── Reset on open / file change ──
  useEffect(() => {
    if (!open) return;
    setCropKey('original');
    setImgScale(1);
    setImgOffset({ x: 0, y: 0 });
    setFilter('Natural');
    setTexts([]);
    setHistory([]);
    setImgNatural({ w: 0, h: 0 });
    setAddingText(false);
    setNewText('');
  }, [open, file]);

  // Create/revoke objectURL for the source image
  useEffect(() => {
    if (!open) return;
    if (file) {
      const url = URL.createObjectURL(file);
      setLocalUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (imageUrl) {
      setLocalUrl(imageUrl);
    }
  }, [open, file, imageUrl]);

  // Reset position when crop ratio changes
  useEffect(() => {
    setImgOffset({ x: 0, y: 0 });
    setImgScale(1);
  }, [cropKey]);

  // ── History ──
  const snap = useCallback(() => ({
    cropKey, imgScale: imgScaleRef.current, imgOffset: { ...imgOffsetRef.current },
    activeFilter, texts: texts.map(t => ({ ...t })),
  }), [cropKey, activeFilter, texts]);

  const pushHistory = useCallback((s) => {
    setHistory(h => [...h.slice(-19), s]);
  }, []);

  const handleUndo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    setCropKey(prev.cropKey);
    setImgScale(prev.imgScale);
    setImgOffset(prev.imgOffset);
    setFilter(prev.activeFilter);
    setTexts(prev.texts);
  };

  const handleReset = () => {
    pushHistory(snap());
    setCropKey('original');
    setImgScale(1);
    setImgOffset({ x: 0, y: 0 });
    setFilter('Natural');
    setTexts([]);
  };

  // ── Crop ratio change ──
  const handleCropRatioChange = (key) => {
    pushHistory(snap());
    setCropKey(key);
  };

  // ── Filter change ──
  const handleFilterChange = (name) => {
    pushHistory(snap());
    setFilter(name);
  };

  // ── Image drag (pointer events + setPointerCapture) ──
  const handleImgPointerDown = useCallback((e) => {
    // Ignore if another pointer is active (2-finger pinch in progress)
    activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointersRef.current.size >= 2) {
      // Start pinch — compute initial distance
      const pts = [...activePointersRef.current.values()];
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      pinchRef.current = { startDist: Math.hypot(dx, dy), startScale: imgScaleRef.current };
      dragRef.current = null;
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      startOX: imgOffsetRef.current.x, startOY: imgOffsetRef.current.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const handleImgPointerMove = useCallback((e) => {
    activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Handle pinch zoom
    if (pinchRef.current && activePointersRef.current.size >= 2) {
      const pts = [...activePointersRef.current.values()];
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy);
      const newScale = Math.max(1, Math.min(5,
        pinchRef.current.startScale * (dist / pinchRef.current.startDist)));
      const { w: dw, h: dh } = imgDisplaySize;
      const { w: fw, h: fh } = cropFrameSize;
      const clamped = clampOffset(imgOffsetRef.current.x, imgOffsetRef.current.y, newScale, dw, dh, fw, fh);
      setImgScale(newScale);
      setImgOffset(clamped);
      return;
    }

    // Handle drag
    if (!dragRef.current) return;
    e.preventDefault();
    const { w: dw, h: dh } = imgDisplaySize;
    const { w: fw, h: fh } = cropFrameSize;
    const clamped = clampOffset(
      dragRef.current.startOX + (e.clientX - dragRef.current.startX),
      dragRef.current.startOY + (e.clientY - dragRef.current.startY),
      imgScaleRef.current, dw, dh, fw, fh,
    );
    setImgOffset(clamped);
  }, [imgDisplaySize, cropFrameSize]);

  const handleImgPointerUp = useCallback((e) => {
    activePointersRef.current.delete(e.pointerId);
    if (activePointersRef.current.size < 2) pinchRef.current = null;
    if (dragRef.current) {
      dragRef.current = null;
      pushHistory(snap());
    }
  }, [snap, pushHistory]);

  // Wheel zoom (desktop)
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    const newScale = Math.max(1, Math.min(5, imgScaleRef.current * factor));
    const { w: dw, h: dh } = imgDisplaySize;
    const { w: fw, h: fh } = cropFrameSize;
    const clamped = clampOffset(imgOffsetRef.current.x, imgOffsetRef.current.y, newScale, dw, dh, fw, fh);
    setImgScale(newScale);
    setImgOffset(clamped);
  }, [imgDisplaySize, cropFrameSize]);

  // Attach wheel listener non-passively (React onWheel is passive in React 17+)
  useEffect(() => {
    const el = cropAreaEl;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel, cropAreaEl]);

  // ── Text operations ──
  const handleAddText = () => {
    if (!newText.trim()) return;
    pushHistory(snap());
    setTexts(prev => [...prev, {
      id: `t${++textIdRef.current}`,
      content: newText.trim(),
      x: 0.5, y: 0.5,
      fontStyle: textFont,
      color: textColor,
      size: textSize,
      shadow: textShadow,
    }]);
    setNewText('');
    setAddingText(false);
  };

  const handleMoveText = useCallback((id, nx, ny) => {
    setTexts(prev => prev.map(t => t.id === id ? { ...t, x: nx, y: ny } : t));
  }, []);

  const handleDeleteText = useCallback((id) => {
    pushHistory(snap());
    setTexts(prev => prev.filter(t => t.id !== id));
  }, [snap, pushHistory]);

  // ── Export + upload ──
  const handleSave = async () => {
    const img = imgRef.current;
    if (!img || !imgLoaded || !cropFrameSize.w) return;
    setSaving(true);
    try {
      const { w: fw } = cropFrameSize;
      const r = sourceRect({ frame: cropFrameSize, display: imgDisplaySize, scale: imgScale, offset: imgOffset, natural: imgNatural });
      const srcX = r.x, srcY = r.y, srcW = r.w, srcH = r.h;

      // Cap output dimensions without upscaling; profile images keep their exact ratio
      const outW = Math.min(Math.round(srcW), profile ? profile.outW : MAX_EXPORT_DIM);
      const outH = profile ? Math.round(outW / profile.ratio) : Math.round(outW * (srcH / srcW));

      const canvas = document.createElement('canvas');
      canvas.width  = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');

      // Draw image with filter baked in
      const filterCss = FILTERS[activeFilter]?.css || 'none';
      if (filterCss !== 'none') ctx.filter = filterCss;
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
      ctx.filter = 'none';

      // Bake text overlays onto canvas
      texts.forEach(t => {
        const fontDef = FONT_STYLES.find(f => f.key === t.fontStyle) || FONT_STYLES[0];
        const fontSize = Math.max(10, Math.round(t.size * (outW / fw)));
        ctx.font = `bold ${fontSize}px ${fontDef.canvas}`;
        ctx.fillStyle = t.color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        if (t.shadow) {
          ctx.shadowColor = 'rgba(0,0,0,0.8)';
          ctx.shadowBlur  = Math.max(2, fontSize * 0.08);
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
        }
        ctx.fillText(t.content, t.x * outW, t.y * outH);
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur  = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      });

      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92));
      if (!blob) throw new Error('Canvas export failed');

      const form = new FormData();
      form.append('file', blob, 'edited.jpg');
      const res = await api.post('/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const abs = res.data.url?.startsWith('/api/')
        ? `${BACKEND_URL}${res.data.url}`
        : res.data.url;

      onSave(abs);
    } catch (err) {
      toast.error('Could not save image. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const imgDisplayW = imgDisplaySize.w * imgScale;
  const imgDisplayH = imgDisplaySize.h * imgScale;

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="sm:max-w-xl bg-card border-border p-0 overflow-hidden [&>button]:hidden"
        style={{ maxHeight: 'min(96dvh, calc(100dvh - var(--header-h, 3.5rem) - 0.5rem))' }}
        data-testid="image-editor-modal"
      >
        <div className="flex flex-col" style={{ maxHeight: 'inherit' }}>

          {/* ── Header ── */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleUndo}
                disabled={!history.length}
                className="mg-btn-icon w-8 h-8 disabled:opacity-30 rounded-lg"
                title="Undo"
                data-testid="editor-undo"
              >
                <Undo2 className="w-4 h-4" />
              </button>
              <button
                onClick={handleReset}
                className="mg-btn-icon w-8 h-8 rounded-lg"
                title="Reset all changes"
                data-testid="editor-reset"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
            <h2 className="font-heading font-bold text-sm">{profile ? profile.title : 'Edit Image'}</h2>
            <button
              onClick={onClose}
              className="mg-btn-icon w-8 h-8 rounded-lg"
              data-testid="editor-close"
              aria-label="Close editor"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* ── Crop area (fixed height, image drag/zoom within) ── */}
          <div
            ref={setCropArea}
            className="flex items-center justify-center bg-black/60 shrink-0"
            style={{
              height: 'min(47dvh, 400px)',
              touchAction: 'none',  // prevent page scroll during image drag
            }}
          >
            {cropFrameSize.w > 0 && (
              <div
                className="relative overflow-hidden"
                style={{
                  width: cropFrameSize.w,
                  height: cropFrameSize.h,
                  outline: '2px solid rgba(255,255,255,0.55)',
                  borderRadius: 2,
                }}
              >
                {localUrl && (
                  <img
                    ref={imgRef}
                    src={localUrl}
                    alt="Editing"
                    draggable={false}
                    crossOrigin="anonymous"
                    onLoad={e => {
                      const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                      setImgNatural({ w, h });
                    }}
                    onPointerDown={handleImgPointerDown}
                    onPointerMove={handleImgPointerMove}
                    onPointerUp={handleImgPointerUp}
                    onPointerCancel={handleImgPointerUp}
                    style={{
                      position: 'absolute',
                      width:  imgDisplayW || '100%',
                      height: imgDisplayH || '100%',
                      left: '50%',
                      top:  '50%',
                      transform: `translate(calc(-50% + ${imgOffset.x}px), calc(-50% + ${imgOffset.y}px))`,
                      filter: FILTERS[activeFilter]?.css || 'none',
                      cursor: 'grab',
                      userSelect: 'none',
                      touchAction: 'none',
                    }}
                  />
                )}
                {mode === 'avatar' && (
                  <div aria-hidden="true" className="absolute inset-0 rounded-full pointer-events-none"
                    style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)', outline: '1px solid rgba(255,255,255,0.7)' }} />
                )}
                {mode === 'banner' && (
                  <div aria-hidden="true" className="absolute inset-y-0 pointer-events-none border-x border-dashed border-white/60"
                    style={{ left: `${(1 - BANNER_PHONE_RATIO / 3) * 50}%`, right: `${(1 - BANNER_PHONE_RATIO / 3) * 50}%` }} />
                )}
                {/* Draggable text overlays */}
                {texts.map(t => (
                  <TextOverlay
                    key={t.id}
                    text={t}
                    frameW={cropFrameSize.w}
                    frameH={cropFrameSize.h}
                    onMove={handleMoveText}
                    onDelete={handleDeleteText}
                    onBeforeMove={() => pushHistory(snap())}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Controls (scrollable) ── */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            {profile && (
              <div className="px-4 py-4 space-y-4" data-testid="profile-editor-controls">
                <label className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="w-10">Zoom</span>
                  <input type="range" min="1" max="4" step="0.01" value={imgScale}
                    onChange={(e) => {
                      const next = parseFloat(e.target.value);
                      setImgScale(next);
                      setImgOffset((o) => clampOffset(o.x, o.y, next, imgDisplaySize.w, imgDisplaySize.h, cropFrameSize.w, cropFrameSize.h));
                    }}
                    className="flex-1 accent-[hsl(var(--mg-purple))]" aria-label="Zoom" data-testid="profile-editor-zoom" />
                </label>
                <p className="text-[12px] text-muted-foreground">Drag to reposition. Pinch or scroll to zoom.{mode === 'banner' ? ' The dashed lines show what phones display.' : ''}</p>
                {imgLoaded && (() => {
                  const rect = sourceRect({ frame: cropFrameSize, display: imgDisplaySize, scale: imgScale, offset: imgOffset, natural: imgNatural });
                  return mode === 'avatar' ? (
                    <div className="flex items-end justify-center gap-6" data-testid="profile-editor-preview">
                      <CropPreview src={localUrl} rect={rect} natural={imgNatural} width={96} height={96} round label="Profile" />
                      <CropPreview src={localUrl} rect={rect} natural={imgNatural} width={40} height={40} round label="Posts" />
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="profile-editor-preview">
                      <CropPreview src={localUrl} rect={rect} natural={imgNatural} width={300} height={100} label="Desktop and tablet" />
                      <CropPreview src={localUrl} rect={{ ...rect, x: rect.x + (rect.w - rect.h * BANNER_PHONE_RATIO) / 2, w: rect.h * BANNER_PHONE_RATIO }}
                        natural={imgNatural} width={200} height={80} label="Phone" />
                    </div>
                  );
                })()}
              </div>
            )}
            {!profile && (<>

            {/* Crop ratio pills */}
            <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide border-b border-border">
              {CROP_RATIOS.map(r => (
                <button
                  key={r.key}
                  onClick={() => handleCropRatioChange(r.key)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap shrink-0 transition-all ${
                    cropKey === r.key
                      ? 'bg-[hsl(var(--mg-purple))] text-white font-medium'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                  data-testid={`crop-ratio-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {/* Filter strip */}
            <div className="px-4 py-3 border-b border-border">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2.5 font-semibold">Filters</p>
              <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
                {Object.entries(FILTERS).map(([name, { css, label }]) => (
                  <button
                    key={name}
                    onClick={() => handleFilterChange(name)}
                    className="flex flex-col items-center gap-1.5 shrink-0"
                    data-testid={`filter-${name.toLowerCase()}`}
                  >
                    <div className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-all ${
                      activeFilter === name
                        ? 'border-[hsl(var(--mg-purple))] scale-105'
                        : 'border-transparent opacity-70 hover:opacity-90'
                    }`}>
                      {localUrl && (
                        <img
                          src={localUrl}
                          alt={label}
                          className="w-full h-full object-cover"
                          style={{ filter: css }}
                        />
                      )}
                    </div>
                    <span className={`text-[10px] ${
                      activeFilter === name ? 'text-foreground font-medium' : 'text-muted-foreground'
                    }`}>
                      {label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Text overlays section */}
            <div className="px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Text</p>
                <button
                  onClick={() => setAddingText(v => !v)}
                  className="flex items-center gap-1 text-xs text-[hsl(var(--mg-purple))] hover:opacity-80 font-medium"
                  data-testid="editor-add-text"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {addingText ? 'Cancel' : 'Add Text'}
                </button>
              </div>

              {addingText && (
                <div className="space-y-2.5 mb-3">
                  <input
                    type="text"
                    placeholder="Enter text…"
                    value={newText}
                    onChange={e => setNewText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddText()}
                    className="w-full px-3 py-2 rounded-lg bg-muted/60 border border-border text-sm focus:outline-none focus:border-primary"
                    autoFocus
                    data-testid="text-input"
                  />
                  {/* Font style */}
                  <div className="flex gap-1.5 flex-wrap">
                    {FONT_STYLES.map(f => (
                      <button
                        key={f.key}
                        onClick={() => setTextFont(f.key)}
                        className={`px-2.5 py-1 rounded text-xs border transition-all ${
                          textFont === f.key
                            ? 'border-[hsl(var(--mg-purple))] text-[hsl(var(--mg-purple))] bg-[hsl(var(--mg-purple)/0.08)]'
                            : 'border-border text-muted-foreground hover:border-muted-foreground'
                        }`}
                        style={{ fontFamily: f.css }}
                      >
                        {f.label}
                      </button>
                    ))}
                    {/* Shadow toggle */}
                    <button
                      onClick={() => setTextShadow(s => !s)}
                      className={`px-2.5 py-1 rounded text-xs border transition-all ${
                        textShadow
                          ? 'border-[hsl(var(--mg-purple))] text-[hsl(var(--mg-purple))] bg-[hsl(var(--mg-purple)/0.08)]'
                          : 'border-border text-muted-foreground'
                      }`}
                    >
                      Shadow
                    </button>
                    {/* Size controls */}
                    <div className="flex items-center gap-1 ml-auto">
                      <button
                        onClick={() => setTextSize(s => Math.max(12, s - 4))}
                        className="w-7 h-7 rounded bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground hover:bg-muted/70"
                      >−</button>
                      <span className="text-xs text-muted-foreground w-7 text-center tabular-nums">{textSize}</span>
                      <button
                        onClick={() => setTextSize(s => Math.min(80, s + 4))}
                        className="w-7 h-7 rounded bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground hover:bg-muted/70"
                      >+</button>
                    </div>
                  </div>
                  {/* Color swatches */}
                  <div className="flex gap-2 items-center">
                    {TEXT_COLORS.map(c => (
                      <button
                        key={c}
                        onClick={() => setTextColor(c)}
                        className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
                        style={{
                          background: c,
                          borderColor: textColor === c ? 'hsl(var(--mg-purple))' : 'rgba(128,128,128,0.4)',
                          boxShadow: c === '#FFFFFF' ? 'inset 0 0 0 1px rgba(0,0,0,0.2)' : 'none',
                        }}
                        aria-label={`Color ${c}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={handleAddText}
                    disabled={!newText.trim()}
                    className="w-full py-2 rounded-lg bg-[hsl(var(--mg-purple))] text-white text-sm font-medium disabled:opacity-40"
                    data-testid="text-confirm"
                  >
                    Add to Image
                  </button>
                </div>
              )}

              {/* List of added texts */}
              {texts.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {texts.map(t => {
                    const f = FONT_STYLES.find(s => s.key === t.fontStyle) || FONT_STYLES[0];
                    return (
                      <div
                        key={t.id}
                        className="flex items-center gap-1.5 bg-muted rounded-full px-2.5 py-1 text-xs max-w-[160px]"
                      >
                        <span
                          className="truncate"
                          style={{ fontFamily: f.css, color: t.color === '#FFFFFF' ? undefined : t.color }}
                        >
                          {t.content}
                        </span>
                        <button
                          onClick={() => handleDeleteText(t.id)}
                          className="text-muted-foreground hover:text-destructive shrink-0"
                          aria-label="Remove text"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            </>)}
          </div>

          {/* ── Footer: Cancel / Save ── */}
          <div
            className="shrink-0 flex gap-2 px-4 py-3 border-t border-border bg-card"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}
          >
            <Button
              variant="ghost"
              className="flex-1 rounded-full"
              onClick={onClose}
              data-testid="editor-cancel"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 rounded-full bg-[hsl(var(--mg-purple))] text-white"
              onClick={handleSave}
              disabled={saving || !imgLoaded}
              data-testid="editor-save"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving…
                </span>
              ) : (
                <><Check className="w-4 h-4 mr-1.5" />{profile ? profile.save : 'Save Changes'}</>
              )}
            </Button>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ImageEditorModal;
